// Pure browser state/storage foundation for the NMA favorites v2 protocol.
// This file owns explicit-membership reducers, the versioned shop-scoped
// storage document, and same-origin coordination. It contains no network
// transport, no Liquid/header integration, and no customer profile data.

export const SCHEMA_VERSION = 2;
export const MAX_ENCODED_BYTES = 2 * 1024 * 1024;
export const MAX_CLEAN_ACCOUNT_SCOPES = 3;
export const DEFAULT_NETWORK_TIMEOUT_MS = 10_000;
export const DEFAULT_DEBOUNCE_MS = 500;
export const DEFAULT_REVALIDATE_THROTTLE_MS = 60_000;
export const DEFAULT_MAX_MUTATION_OPERATIONS = 32;
export const DEFAULT_MAX_IMPORT_OPERATIONS = 100;
export const MAX_PROMPT_CONFLICT_RETRIES = 2;
export const DEFAULT_BACKOFF_MS = [1_000, 2_000, 5_000, 15_000, 30_000];

export function fallbackUuid() {
  if (globalThis.crypto?.randomUUID) {
    return globalThis.crypto.randomUUID();
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export function storageKey(shopId) {
  return `nma:favorites:v2:${shopId}`;
}

export function emptyDocument() {
  return {
    schemaVersion: SCHEMA_VERSION,
    documentRevision: 0,
    guest: { generation: fallbackUuid(), items: [] },
    accounts: {},
  };
}

export function normalizeItems(items) {
  const seen = new Set();
  const result = [];
  for (const item of items ?? []) {
    if (!item || typeof item.productId !== "string") {
      continue;
    }
    if (seen.has(item.productId)) {
      continue;
    }
    seen.add(item.productId);
    result.push({
      productId: item.productId,
      handle: typeof item.handle === "string" ? item.handle : undefined,
      addedAt: typeof item.addedAt === "number" ? item.addedAt : undefined,
    });
  }
  return result;
}

// Re-saving an existing item keeps its position. A new item is added to the
// front; removing and re-adding follows the same most-recent-first behavior.
export function applyGuestSet(items, input) {
  const next = normalizeItems(items);
  const index = next.findIndex((item) => item.productId === input.productId);

  if (input.present) {
    if (index === -1) {
      next.unshift({
        productId: input.productId,
        handle: input.handle,
        addedAt: input.addedAt,
      });
    } else if (input.handle !== undefined) {
      next[index] = { ...next[index], handle: input.handle };
    }
  } else if (index !== -1) {
    next.splice(index, 1);
  }

  return next;
}

// Unsent account intents coalesce to the latest explicit membership per
// product, preserving newest-intent order.
export function applyAccountDraft(drafts, input) {
  const next = (drafts ?? []).filter(
    (draft) => draft.productId !== input.productId,
  );
  next.push({
    intentId: fallbackUuid(),
    productId: input.productId,
    handle: input.handle,
    present: input.present,
  });
  return next;
}

function applyMembershipOperation(list, operation) {
  const index = list.indexOf(operation.productId);
  if (operation.present) {
    if (index === -1) {
      list.unshift(operation.productId);
    }
  } else if (index !== -1) {
    list.splice(index, 1);
  }
  return list;
}

// Visible account membership = confirmed snapshot plus the immutable pending
// batch plus any unsent drafts.
export function computeAccountItems(scope) {
  const list = [...(scope?.snapshot?.productIds ?? [])];
  for (const operation of scope?.inFlight?.operations ?? []) {
    applyMembershipOperation(list, operation);
  }
  for (const draft of scope?.drafts ?? []) {
    applyMembershipOperation(list, {
      productId: draft.productId,
      present: draft.present,
    });
  }
  return list;
}

export function createBatch({
  kind,
  subject,
  operations,
  expectedVersion,
  capturedIntentIds,
  uuid = fallbackUuid,
  now = Date.now,
}) {
  return {
    protocolVersion: 2,
    expectedSubject: subject,
    batchId: uuid(),
    expectedVersion,
    kind,
    operations: operations.map((operation) => ({
      productId: operation.productId,
      present: operation.present,
    })),
    capturedIntentIds: [...(capturedIntentIds ?? [])],
    firstAttemptAt: now(),
    attemptCount: 0,
    conflictRetries: 0,
    hadUnknownOutcome: false,
    lastErrorCategory: null,
  };
}

export function measureEncodedBytes(document) {
  try {
    return new TextEncoder().encode(JSON.stringify(document)).byteLength;
  } catch {
    return 0;
  }
}

export function hasRecoveryData(scope) {
  return Boolean(
    scope &&
      ((scope.drafts?.length ?? 0) > 0 ||
        scope.inFlight ||
        scope.guestImport ||
        scope.needsReview),
  );
}

// Evict only eligible clean account caches (no drafts, no inFlight, no
// import). Recovery data is never evicted to satisfy a size bound.
export function evictEligibleAccounts(accounts, max = MAX_CLEAN_ACCOUNT_SCOPES) {
  const entries = Object.entries(accounts ?? {});
  const clean = entries.filter(([, scope]) => !hasRecoveryData(scope));
  if (clean.length <= max) {
    return accounts;
  }
  clean.sort(
    ([, a], [, b]) => (a.updatedAt ?? 0) - (b.updatedAt ?? 0),
  );
  const evict = new Set(clean.slice(0, clean.length - max).map(([subject]) => subject));
  const result = {};
  for (const [subject, scope] of entries) {
    if (!evict.has(subject)) {
      result[subject] = scope;
    }
  }
  return result;
}

export function createDefaultAdapters(shopId) {
  return {
    storage: {
      get() {
        try {
          const raw = globalThis.localStorage?.getItem(storageKey(shopId));
          return raw ? JSON.parse(raw) : null;
        } catch {
          return null;
        }
      },
      set(value) {
        globalThis.localStorage?.setItem(storageKey(shopId), JSON.stringify(value));
      },
    },
    locks: {
      request(name, callback) {
        if (globalThis.navigator?.locks?.request) {
          return globalThis.navigator.locks.request(name, callback);
        }
        return callback();
      },
    },
    now: () => Date.now(),
    uuid: fallbackUuid,
    setTimeout: (callback, ms) => globalThis.setTimeout(callback, ms),
    clearTimeout: (id) => globalThis.clearTimeout(id),
    addEventListener: (target, event, handler) => {
      target?.addEventListener?.(event, handler);
    },
    removeEventListener: (target, event, handler) => {
      target?.removeEventListener?.(event, handler);
    },
  };
}

export function createBrowserTransport({
  statePath,
  mutationsPath,
  resolvePath,
  fetchImpl = globalThis.fetch,
  credentials = "same-origin",
}) {
  if (!statePath || !mutationsPath) {
    throw new Error(
      "createBrowserTransport requires statePath and mutationsPath.",
    );
  }
  return {
    state: ({ signal } = {}) =>
      fetchImpl(statePath, {
        method: "GET",
        credentials,
        headers: { Accept: "application/json" },
        signal,
      }),
    mutate: ({ body, signal } = {}) =>
      fetchImpl(mutationsPath, {
        method: "POST",
        credentials,
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
        signal,
      }),
    resolve: ({ body, signal } = {}) =>
      fetchImpl(resolvePath, {
        method: "POST",
        credentials,
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
        signal,
      }),
  };
}

function frozen(value) {
  if (value === null || typeof value !== "object") {
    return value;
  }
  if (Array.isArray(value)) {
    return Object.freeze(value.map(frozen));
  }
  const out = {};
  for (const key of Object.keys(value)) {
    out[key] = frozen(value[key]);
  }
  return Object.freeze(out);
}

function emptyScope(now) {
  return {
    snapshot: null,
    handleHints: {},
    drafts: [],
    inFlight: null,
    guestImport: null,
    needsReview: null,
    updatedAt: now(),
  };
}

function normalizeReason(reason) {
  if (reason && typeof reason === "object") {
    return reason;
  }
  return { reason: reason ?? "manual" };
}

function snapshotFromState(state, now) {
  return {
    version: state.version,
    revision: state.revision,
    productIds: [...(state.productIds ?? [])],
    count: state.productIds?.length ?? 0,
    fetchedAt: now(),
  };
}

function parseCsrfExpiry(value) {
  if (typeof value !== "string") {
    return null;
  }
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : null;
}

export function createFavoritesManager({
  shopId,
  capabilities = {},
  adapters = createDefaultAdapters(shopId),
  transport = null,
  syncEnabled = false,
  autoBootstrap = false,
  debounceDelay = DEFAULT_DEBOUNCE_MS,
  networkTimeout = DEFAULT_NETWORK_TIMEOUT_MS,
  revalidateThrottleMs = DEFAULT_REVALIDATE_THROTTLE_MS,
  backoffSchedule = DEFAULT_BACKOFF_MS,
  maxMutationOperations =
    capabilities.maxMutationOperations ?? DEFAULT_MAX_MUTATION_OPERATIONS,
  maxImportOperations =
    capabilities.maxImportOperations ?? DEFAULT_MAX_IMPORT_OPERATIONS,
}) {
  let maxFavorites = capabilities.maxFavorites ?? 1000;
  const listeners = new Set();
  const csrfBySubject = new Map();
  const drainDebounces = new Map();
  const retryTimers = new Map();

  let identity = "unknown";
  let subject = null;
  let persistence = "durable";
  let document = null;
  let unboundIntents = [];
  let identityGeneration = 0;
  let stateEpoch = 0;
  let bootstrapStatus = "idle";
  let bootstrapPromise = null;
  let bootstrapQueued = false;
  let lastRevalidateAt = 0;
  let disposed = false;
  let lifecycleBound = false;

  function currentSync() {
    if (!syncEnabled) {
      return "idle";
    }
    if (identity === "unknown") {
      return bootstrapStatus === "loading" ? "loading" : "offline";
    }
    if (identity === "account" && subject) {
      const scope = document?.accounts?.[subject];
      if (scope?.guestImport?.status === "blocked") {
        return "import_blocked_limit";
      }
      if (scope?.guestImport?.status === "needs_review") {
        return "needs_review";
      }
      if (scope?.needsReview) {
        return "needs_review";
      }
      if (scope?.inFlight) {
        return "saving";
      }
      if ((scope?.drafts?.length ?? 0) > 0) {
        return "pending";
      }
    }
    return "idle";
  }

  function notify() {
    if (disposed) {
      return;
    }
    const snapshot = getSnapshot();
    for (const listener of [...listeners]) {
      try {
        listener(snapshot);
      } catch {
        // listener errors never break other subscribers
      }
    }
  }

  function effectiveItems() {
    if (identity === "guest") {
      return normalizeItems(document?.guest?.items ?? []);
    }
    if (identity === "account" && subject) {
      return computeAccountItems(document?.accounts?.[subject]).map(
        (productId) => ({ productId }),
      );
    }
    return [];
  }

  function getSnapshot() {
    const items = effectiveItems();
    const accountScope =
      identity === "account" && subject
        ? document?.accounts?.[subject]
        : undefined;
    return frozen({
      identity,
      subject,
      persistence,
      sync: currentSync(),
      items,
      pendingItems: unboundIntents.map((intent) => ({
        productId: intent.productId,
        present: intent.present,
      })),
      count: items.length,
      overLimit: items.length > maxFavorites,
      hasPending:
        unboundIntents.length > 0 ||
        Boolean(accountScope?.inFlight) ||
        (accountScope?.drafts?.length ?? 0) > 0 ||
        Boolean(accountScope?.needsReview) ||
        Boolean(
          accountScope?.guestImport &&
            accountScope.guestImport.status !== "complete",
        ),
      hasUnbound: unboundIntents.length > 0,
      inFlight: accountScope?.inFlight ?? null,
      guestImport: accountScope?.guestImport ?? null,
      needsReview: accountScope?.needsReview ?? null,
    });
  }

  function refresh() {
    let stored;
    try {
      stored = adapters.storage.get();
    } catch {
      if (!document) {
        document = emptyDocument();
      }
      persistence = "memory";
      return;
    }

    if (stored && stored.schemaVersion === SCHEMA_VERSION) {
      document = stored;
    } else if (!document) {
      document = emptyDocument();
    }
    persistence = "durable";
  }

  async function withStateLock(callback) {
    return adapters.locks.request(
      `nma:favorites:state:${shopId}`,
      async () => {
        refresh();
        return callback();
      },
    );
  }

  function persist() {
    if (!document) {
      document = emptyDocument();
    }
    document.documentRevision = (document.documentRevision ?? 0) + 1;
    if (measureEncodedBytes(document) > MAX_ENCODED_BYTES) {
      document.accounts = evictEligibleAccounts(document.accounts);
    }
    try {
      adapters.storage.set(document);
      persistence = "durable";
    } catch {
      persistence = "memory";
    }
  }

  function ensureScope(subjectId) {
    const scope = (document.accounts[subjectId] ??= emptyScope(adapters.now));
    return scope;
  }

  function cancelDrain(subjectId) {
    const timer = drainDebounces.get(subjectId);
    if (timer !== undefined) {
      adapters.clearTimeout(timer);
      drainDebounces.delete(subjectId);
    }
  }

  function cancelRetry(subjectId) {
    const timer = retryTimers.get(subjectId);
    if (timer !== undefined) {
      adapters.clearTimeout(timer);
      retryTimers.delete(subjectId);
    }
  }

  function setIdentity(next, nextSubject = null) {
    const previousSubject = subject;
    const changed = next !== identity || nextSubject !== subject;

    if (next === "unknown") {
      unboundIntents = [];
    }

    identity = next;
    subject = nextSubject;
    if (changed) {
      identityGeneration += 1;
    }

    if (next === "account" && nextSubject) {
      ensureScope(nextSubject);
    }

    if (previousSubject && previousSubject !== nextSubject) {
      cancelDrain(previousSubject);
      cancelRetry(previousSubject);
    }

    if (changed) {
      notify();
    }
  }

  function invalidateIdentity() {
    const previousSubject = subject;
    if (previousSubject) {
      cancelDrain(previousSubject);
      cancelRetry(previousSubject);
    }
    identity = "unknown";
    subject = null;
    unboundIntents = [];
    identityGeneration += 1;
    bootstrapStatus = "idle";
    if (bootstrapPromise) {
      bootstrapQueued = true;
    }
    notify();
  }

  function attachUnboundToGuest() {
    if (unboundIntents.length === 0) {
      return;
    }
    const intents = unboundIntents;
    unboundIntents = [];
    for (const intent of intents) {
      document.guest.items = applyGuestSet(document.guest.items, {
        productId: intent.productId,
        handle: intent.handle,
        present: intent.present,
        addedAt: adapters.now(),
      });
    }
  }

  function attachUnboundToAccount(subjectId) {
    if (unboundIntents.length === 0) {
      return;
    }
    const scope = ensureScope(subjectId);
    const intents = unboundIntents;
    unboundIntents = [];
    for (const intent of intents) {
      scope.drafts = applyAccountDraft(scope.drafts, {
        productId: intent.productId,
        handle: intent.handle,
        present: intent.present,
      });
    }
    scope.updatedAt = adapters.now();
  }

  function setBootstrapStatus(next) {
    bootstrapStatus = next;
    notify();
  }

  async function parseTransportResult(result) {
    if (!result) {
      throw new Error("empty transport response");
    }
    if (typeof result.status === "number") {
      let body;
      if (typeof result.json === "function") {
        try {
          body = await result.json();
        } catch {
          body = null;
        }
      } else {
        body = result.body;
      }
      return { status: result.status, body };
    }
    if (
      typeof result === "object" &&
      "status" in result &&
      "body" in result
    ) {
      return { status: result.status, body: result.body };
    }
    return { status: 200, body: result };
  }

  function createRequestSignal() {
    const controller =
      typeof AbortController !== "undefined" ? new AbortController() : null;
    let timer;
    if (controller && networkTimeout > 0) {
      timer = adapters.setTimeout(() => controller.abort(), networkTimeout);
    }
    return {
      signal: controller?.signal,
      cleanup() {
        if (timer !== undefined) {
          adapters.clearTimeout(timer);
        }
      },
    };
  }

  function validateStatePayload(body) {
    if (!body || body.protocolVersion !== SCHEMA_VERSION) {
      return false;
    }
    if (body.identity?.kind === "account") {
      return (
        typeof body.identity.subject === "string" &&
        body.state &&
        typeof body.state.version === "string" &&
        Array.isArray(body.state.productIds)
      );
    }
    if (body.identity?.kind === "guest") {
      return true;
    }
    return false;
  }

  function updateCapabilities(payload) {
    const caps = payload?.capabilities;
    if (!caps) {
      return;
    }
    if (Number.isSafeInteger(caps.maxFavorites)) {
      maxFavorites = caps.maxFavorites;
    }
    if (Number.isSafeInteger(caps.maxMutationOperations)) {
      maxMutationOperations = caps.maxMutationOperations;
    }
    if (Number.isSafeInteger(caps.maxImportOperations)) {
      maxImportOperations = caps.maxImportOperations;
    }
  }

  function applyAccountState(subjectId, payload) {
    const scope = ensureScope(subjectId);
    updateCapabilities(payload);
    scope.snapshot = snapshotFromState(payload.state, adapters.now);
    scope.updatedAt = adapters.now();
    if (typeof payload.csrfToken === "string") {
      csrfBySubject.set(subjectId, {
        token: payload.csrfToken,
        expiresAt: parseCsrfExpiry(payload.csrfExpiresAt),
      });
    }
    attachUnboundToAccount(subjectId);
    if (!scope.guestImport && document.guest?.items?.length) {
      prepareGuestImport(subjectId);
    }
  }

  function prepareGuestImport(subjectId) {
    const scope = ensureScope(subjectId);
    if (scope.guestImport) {
      return;
    }
    const items = normalizeItems(document.guest?.items ?? []);
    if (items.length === 0) {
      document.guest = { generation: fallbackUuid(), items: [] };
      return;
    }
    scope.guestImport = {
      sourceGeneration: document.guest.generation,
      items,
      acknowledgedProductIds: [],
      rejectedProductIds: [],
      rejectedItems: [],
      status: "preflight",
      linkedBatchId: null,
      createdAt: adapters.now(),
    };
    document.guest = { generation: fallbackUuid(), items: [] };
    scope.updatedAt = adapters.now();
    persist();
  }

  function preflightGuestImport(subjectId) {
    const scope = document.accounts[subjectId];
    const importRecord = scope?.guestImport;
    if (!importRecord || importRecord.status === "complete") {
      return true;
    }

    const existing = new Set(scope.snapshot?.productIds ?? []);
    const newItems = importRecord.items.filter(
      (item) => !existing.has(item.productId),
    );
    const alreadyPresent = importRecord.items.filter((item) =>
      existing.has(item.productId),
    );

    if (alreadyPresent.length > 0) {
      importRecord.acknowledgedProductIds.push(
        ...alreadyPresent.map((item) => item.productId),
      );
      importRecord.items = newItems;
    }

    const currentCount = scope.snapshot?.productIds?.length ?? 0;
    const projectedCount = currentCount + newItems.length;
    if (projectedCount > maxFavorites) {
      importRecord.status = "blocked";
      scope.updatedAt = adapters.now();
      persist();
      return false;
    }

    importRecord.status = importRecord.items.length > 0 ? "ready" : "complete";
    scope.updatedAt = adapters.now();
    persist();
    return true;
  }

  function freezeEditBatch(subjectId) {
    const scope = document.accounts[subjectId];
    const drafts = scope.drafts.slice(0, maxMutationOperations);
    if (drafts.length === 0) {
      return null;
    }
    const batch = createBatch({
      kind: "edit",
      subject: subjectId,
      expectedVersion: scope.snapshot?.version,
      operations: drafts.map((draft) => ({
        productId: draft.productId,
        present: draft.present,
      })),
      capturedIntentIds: drafts.map((draft) => draft.intentId),
      uuid: adapters.uuid,
      now: adapters.now,
    });
    scope.inFlight = batch;
    scope.drafts = scope.drafts.slice(drafts.length);
    scope.updatedAt = adapters.now();
    persist();
    return batch;
  }

  function freezeImportChunk(subjectId) {
    const scope = document.accounts[subjectId];
    const importRecord = scope.guestImport;
    const remaining = importRecord.items;
    if (remaining.length === 0) {
      importRecord.status = "complete";
      scope.updatedAt = adapters.now();
      persist();
      return null;
    }

    // Frozen guest items are stored most-recent-first. Chunks are dispatched
    // oldest-to-newest to preserve relative order across chunk boundaries.
    const chunk = [...remaining].reverse().slice(0, maxImportOperations);
    const batch = createBatch({
      kind: "guest_import",
      subject: subjectId,
      expectedVersion: scope.snapshot?.version,
      operations: chunk.map((item) => ({
        productId: item.productId,
        present: true,
      })),
      capturedIntentIds: chunk.map((item) => item.productId),
      uuid: adapters.uuid,
      now: adapters.now,
    });
    scope.inFlight = batch;
    importRecord.linkedBatchId = batch.batchId;
    scope.updatedAt = adapters.now();
    persist();
    return batch;
  }

  function setNeedsReview(subjectId, batch) {
    const scope = document.accounts[subjectId];
    if (!scope) {
      return;
    }
    scope.inFlight = null;
    scope.needsReview = batch;
    scope.updatedAt = adapters.now();
    persist();
  }

  function applyAcknowledgment(subjectId, batch, body) {
    const scope = document.accounts[subjectId];
    if (!scope) {
      return;
    }
    if (body.state) {
      scope.snapshot = snapshotFromState(body.state, adapters.now);
    }

    if (batch.kind === "guest_import" && scope.guestImport) {
      const acknowledged = new Set(
        batch.operations.map((operation) => operation.productId),
      );
      scope.guestImport.acknowledgedProductIds.push(...acknowledged);
      scope.guestImport.items = scope.guestImport.items.filter(
        (item) => !acknowledged.has(item.productId),
      );
      scope.guestImport.linkedBatchId = null;
      scope.guestImport.status =
        scope.guestImport.items.length > 0 ? "ready" : "complete";
    }

    scope.inFlight = null;
    scope.updatedAt = adapters.now();
    stateEpoch += 1;
    persist();
  }

  function applyConflict(subjectId, batch, body) {
    const scope = document.accounts[subjectId];
    if (!scope) {
      return;
    }
    if (body.state) {
      scope.snapshot = snapshotFromState(body.state, adapters.now);
      stateEpoch += 1;
    }

    if (batch.hadUnknownOutcome) {
      setNeedsReview(subjectId, batch);
      return;
    }
    if ((batch.conflictRetries ?? 0) >= MAX_PROMPT_CONFLICT_RETRIES) {
      setNeedsReview(subjectId, batch);
      return;
    }

    const replacement = createBatch({
      kind: batch.kind,
      subject: subjectId,
      expectedVersion: body.state?.version ?? scope.snapshot?.version,
      operations: batch.operations,
      capturedIntentIds: batch.capturedIntentIds,
      uuid: adapters.uuid,
      now: adapters.now,
    });
    replacement.conflictRetries = (batch.conflictRetries ?? 0) + 1;
    scope.inFlight = replacement;
    if (scope.guestImport) {
      scope.guestImport.linkedBatchId = replacement.batchId;
    }
    scope.updatedAt = adapters.now();
    persist();
  }

  function markUnknown(subjectId, batch) {
    const scope = document.accounts[subjectId];
    if (!scope) {
      return null;
    }
    const updated = {
      ...batch,
      attemptCount: (batch.attemptCount ?? 0) + 1,
      hadUnknownOutcome: true,
      lastErrorCategory: "unknown",
      lastAttemptAt: adapters.now(),
    };
    scope.inFlight = updated;
    scope.updatedAt = adapters.now();
    persist();
    return updated;
  }

  function scheduleRetry(subjectId, batch) {
    cancelRetry(subjectId);
    if (disposed || identity !== "account" || subject !== subjectId) {
      return;
    }
    const attemptCount = batch.attemptCount ?? 0;
    if (attemptCount > backoffSchedule.length) {
      return;
    }
    const base = backoffSchedule[Math.max(0, attemptCount - 1)];
    const jitter = base * (0.75 + Math.random() * 0.5);
    const delay = Math.max(0, Math.round(jitter));
    const timer = adapters.setTimeout(() => {
      retryTimers.delete(subjectId);
      void drain(subjectId);
    }, delay);
    retryTimers.set(subjectId, timer);
  }

  async function getFreshCsrf(subjectId, startGeneration) {
    if (identity !== "account" || subject !== subjectId) {
      return null;
    }
    const cached = csrfBySubject.get(subjectId);
    if (
      cached?.token &&
      (cached.expiresAt === null ||
        cached.expiresAt - adapters.now() > 5_000)
    ) {
      return cached.token;
    }

    const request = createRequestSignal();
    let result;
    try {
      result = await transport.state({ signal: request.signal });
    } catch {
      return null;
    } finally {
      request.cleanup();
    }
    const parsed = await parseTransportResult(result).catch(() => null);
    if (!parsed || !validateStatePayload(parsed.body)) {
      return null;
    }
    const body = parsed.body;
    if (body.identity?.kind !== "account") {
      return null;
    }
    if (identityGeneration !== startGeneration || subject !== subjectId) {
      return null;
    }
    if (body.identity.subject !== subjectId) {
      return null;
    }
    if (typeof body.csrfToken === "string") {
      csrfBySubject.set(subjectId, {
        token: body.csrfToken,
        expiresAt: parseCsrfExpiry(body.csrfExpiresAt),
      });
    }
    return csrfBySubject.get(subjectId)?.token ?? null;
  }

  function handleAuthFailure(status, body) {
    if (status === 401 || status === 403 || body?.error?.code === "SUBJECT_CHANGED") {
      invalidateIdentity();
      if (syncEnabled && transport) {
        void bootstrap();
      }
      return true;
    }
    return false;
  }

  async function sendBatch(subjectId, batch) {
    const startGeneration = identityGeneration;
    const csrfToken = await getFreshCsrf(subjectId, startGeneration);
    if (!csrfToken) {
      if (identity !== "account" || subject !== subjectId) {
        return;
      }
      const marked = markUnknown(subjectId, batch);
      if (marked) {
        scheduleRetry(subjectId, marked);
      }
      return false;
    }

    if (identity !== "account" || subject !== subjectId) {
      return false;
    }

    const body = {
      protocolVersion: SCHEMA_VERSION,
      expectedSubject: subjectId,
      csrfToken,
      batchId: batch.batchId,
      expectedVersion: batch.expectedVersion,
      kind: batch.kind,
      operations: batch.operations,
    };
    const request = createRequestSignal();
    let result;
    try {
      result = await transport.mutate({
        body,
        signal: request.signal,
      });
    } catch {
      const marked = markUnknown(subjectId, batch);
      if (marked) {
        scheduleRetry(subjectId, marked);
      }
      return false;
    } finally {
      request.cleanup();
    }

    let parsed;
    try {
      parsed = await parseTransportResult(result);
    } catch {
      const marked = markUnknown(subjectId, batch);
      if (marked) {
        scheduleRetry(subjectId, marked);
      }
      return false;
    }

    if (identity !== "account" || subject !== subjectId) {
      return false;
    }
    if (!parsed || typeof parsed.status !== "number") {
      const marked = markUnknown(subjectId, batch);
      if (marked) {
        scheduleRetry(subjectId, marked);
      }
      return false;
    }

    const { status, body: responseBody } = parsed;

    if (status === 200) {
      if (!validateStatePayload(responseBody)) {
        const marked = markUnknown(subjectId, batch);
        if (marked) {
          scheduleRetry(subjectId, marked);
        }
        return false;
      }
      applyAcknowledgment(subjectId, batch, responseBody);
      notify();
      return true;
    }

    if (handleAuthFailure(status, responseBody)) {
      return false;
    }

    const code = responseBody?.error?.code;
    if (status === 409 && code === "VERSION_CONFLICT") {
      applyConflict(subjectId, batch, responseBody);
      notify();
      return Boolean(document.accounts[subjectId]?.inFlight);
    }

    if (status === 409 && code === "BATCH_ID_REUSED") {
      setNeedsReview(subjectId, batch);
      notify();
      return false;
    }

    if (status === 409 && code === "LIMIT_EXCEEDED") {
      const scope = document.accounts[subjectId];
      if (scope) {
        if (responseBody.state) {
          scope.snapshot = snapshotFromState(responseBody.state, adapters.now);
        }
        if (scope.guestImport) {
          scope.guestImport.status = "blocked";
        } else {
          setNeedsReview(subjectId, batch);
        }
        scope.inFlight = null;
        scope.updatedAt = adapters.now();
        persist();
      }
      notify();
      return false;
    }

    if (status === 422 && code === "PRODUCT_UNAVAILABLE") {
      const scope = document.accounts[subjectId];
      if (scope?.guestImport) {
        const rejected = new Set(responseBody?.rejectedProductIds ?? []);
        const rejectedItems = scope.guestImport.items.filter((item) =>
          rejected.has(item.productId),
        );
        scope.guestImport.rejectedProductIds.push(...rejected);
        scope.guestImport.rejectedItems.push(...rejectedItems);
        scope.guestImport.items = scope.guestImport.items.filter(
          (item) => !rejected.has(item.productId),
        );
        scope.guestImport.status =
          scope.guestImport.items.length > 0 ? "ready" : "needs_review";
        scope.guestImport.linkedBatchId = null;
        scope.inFlight = null;
        scope.updatedAt = adapters.now();
        persist();
        notify();
        return scope.guestImport.items.length > 0;
      } else {
        setNeedsReview(subjectId, batch);
        notify();
        return false;
      }
    }

    if (status === 426) {
      setNeedsReview(subjectId, batch);
      notify();
      return false;
    }

    // 429, 503, malformed/truncated bodies, and other non-definitive outcomes
    // are unknown outcomes with the same immutable semantic batch.
    const marked = markUnknown(subjectId, batch);
    if (marked) {
      scheduleRetry(subjectId, marked);
    }
    return false;
  }

  async function drainLocked(subjectId) {
    while (true) {
      refresh();
      if (identity !== "account" || subject !== subjectId) {
        return;
      }
      const scope = document.accounts[subjectId];
      if (!scope) {
        return;
      }

      if (scope.inFlight) {
        const shouldContinue = await sendBatch(subjectId, scope.inFlight);
        if (!shouldContinue) {
          return;
        }
        continue;
      }

      if (scope.guestImport) {
        preflightGuestImport(subjectId);
        if (scope.guestImport.status === "blocked") {
          notify();
          return;
        }
        if (scope.guestImport.status === "ready") {
          const batch = freezeImportChunk(subjectId);
          if (batch) {
            const shouldContinue = await sendBatch(subjectId, batch);
            if (!shouldContinue) {
              return;
            }
            continue;
          }
        }
      }

      if ((scope.drafts?.length ?? 0) > 0) {
        const batch = freezeEditBatch(subjectId);
        if (batch) {
          const shouldContinue = await sendBatch(subjectId, batch);
          if (!shouldContinue) {
            return;
          }
          continue;
        }
      }

      return;
    }
  }

  async function drain(subjectId = subject) {
    if (!syncEnabled || !transport || identity !== "account" || !subjectId) {
      return;
    }
    await adapters.locks.request(
      `nma:favorites:sync:${shopId}:${subjectId}`,
      () => drainLocked(subjectId),
    );
  }

  function scheduleDrain(subjectId) {
    if (!syncEnabled || !transport) {
      return;
    }
    cancelDrain(subjectId);
    const timer = adapters.setTimeout(() => {
      drainDebounces.delete(subjectId);
      void drain(subjectId);
    }, debounceDelay);
    drainDebounces.set(subjectId, timer);
  }

  async function requestState({ expectedSubject = null, apply = true } = {}) {
    const startIdentityGeneration = identityGeneration;
    const startStateEpoch = stateEpoch;
    const request = createRequestSignal();
    let result;
    try {
      result = await transport.state({ signal: request.signal });
    } catch {
      return { ok: false, offline: true };
    } finally {
      request.cleanup();
    }

    let parsed;
    try {
      parsed = await parseTransportResult(result);
    } catch {
      return { ok: false, offline: true };
    }

    if (
      !parsed ||
      typeof parsed.status !== "number" ||
      parsed.status < 200 ||
      parsed.status >= 300 ||
      !validateStatePayload(parsed.body)
    ) {
      if (apply && handleAuthFailure(parsed?.status, parsed?.body)) {
        return { ok: false, auth: true };
      }
      return { ok: false, offline: parsed?.status >= 500 };
    }

    const body = parsed.body;
    if (
      expectedSubject &&
      body.identity?.subject &&
      body.identity.subject !== expectedSubject
    ) {
      return { ok: false, subjectMismatch: true };
    }

    if (!apply) {
      return { ok: true, body };
    }

    const canApply =
      identityGeneration === startIdentityGeneration &&
      stateEpoch === startStateEpoch;
    if (!canApply) {
      return { ok: true, stale: true, body };
    }

    await withStateLock(async () => {
      if (body.identity?.kind === "guest") {
        identity = "guest";
        subject = null;
        updateCapabilities(body);
        attachUnboundToGuest();
        persist();
        stateEpoch += 1;
      } else if (body.identity?.kind === "account") {
        identity = "account";
        subject = body.identity.subject;
        applyAccountState(subject, body);
        persist();
        stateEpoch += 1;
      }
    });
    notify();
    return { ok: true, body };
  }

  async function bootstrap() {
    if (!syncEnabled || !transport) {
      return;
    }
    if (bootstrapPromise) {
      bootstrapQueued = true;
      return bootstrapPromise;
    }
    bootstrapQueued = false;
    setBootstrapStatus("loading");
    bootstrapPromise = (async () => {
      const result = await requestState({ apply: true });
      if (disposed) {
        return;
      }
      if (!result.ok) {
        setBootstrapStatus("offline");
        return;
      }
      setBootstrapStatus("idle");
      if (identity === "account" && subject) {
        scheduleDrain(subject);
      }
    })();
    try {
      await bootstrapPromise;
    } finally {
      bootstrapPromise = null;
      if (bootstrapQueued && !disposed && identity === "unknown") {
        bootstrapQueued = false;
        void bootstrap();
      }
    }
  }

  function handlePageShow(persisted) {
    if (persisted) {
      invalidateIdentity();
      void bootstrap();
      return;
    }
    void revalidate({ reason: "pageshow" });
  }

  function handleFocus() {
    void revalidate({ reason: "focus" });
  }

  function handleOnline() {
    if (identity === "account" && subject) {
      cancelRetry(subject);
      void drain(subject);
    }
  }

  function bindLifecycle() {
    if (lifecycleBound || disposed) {
      return;
    }
    lifecycleBound = true;
    adapters.addEventListener(globalThis, "pageshow", (event) => {
      handlePageShow(Boolean(event?.persisted));
    });
    adapters.addEventListener(globalThis, "focus", handleFocus);
    adapters.addEventListener(globalThis, "online", handleOnline);
    adapters.addEventListener(globalThis, "storage", () => {
      void revalidate({ reason: "storage" });
    });
  }

  function unbindLifecycle() {
    if (!lifecycleBound) {
      return;
    }
    lifecycleBound = false;
    adapters.removeEventListener(globalThis, "pageshow", handlePageShow);
    adapters.removeEventListener(globalThis, "focus", handleFocus);
    adapters.removeEventListener(globalThis, "online", handleOnline);
  }

  async function set(input, present) {
    const productId = String(input.productId);

    if (identity === "unknown") {
      unboundIntents.push({
        productId,
        handle: input.handle,
        present,
      });
      if (syncEnabled && transport && bootstrapStatus !== "loading") {
        void bootstrap();
      }
      notify();
      return;
    }

    await withStateLock(() => {
      if (identity === "guest") {
        document.guest.items = applyGuestSet(document.guest.items, {
          productId,
          handle: input.handle,
          present,
          addedAt: adapters.now(),
        });
        persist();
      } else if (identity === "account" && subject) {
        const scope = ensureScope(subject);
        scope.drafts = applyAccountDraft(scope.drafts, {
          productId,
          handle: input.handle,
          present,
        });
        scope.updatedAt = adapters.now();
        persist();
      }
    });
    if (identity === "account" && subject) {
      scheduleDrain(subject);
    }
    notify();
  }

  async function toggle(input) {
    return set(input, !has(input.productId));
  }

  function has(productId) {
    const target = String(productId);
    return effectiveItems().some((item) => item.productId === target);
  }

  function subscribe(listener) {
    listeners.add(listener);
    return () => listeners.delete(listener);
  }

  async function revalidate(reason) {
    if (!syncEnabled || !transport) {
      refresh();
      notify();
      return;
    }
    const normalized = normalizeReason(reason);
    const type = normalized.reason ?? "manual";
    if (type === "storage") {
      refresh();
      notify();
      return;
    }
    if (type === "pageshow") {
      if (normalized.persisted) {
        invalidateIdentity();
        await bootstrap();
      }
      return;
    }
    if (type === "focus") {
      const now = adapters.now();
      if (now - lastRevalidateAt < revalidateThrottleMs) {
        return;
      }
      lastRevalidateAt = now;
    }
    if (identity === "unknown") {
      await bootstrap();
      return;
    }
    await requestState({
      expectedSubject: subject,
      apply: true,
    });
    if (identity === "account" && subject) {
      scheduleDrain(subject);
    }
  }

  async function resolveHandles(productIds) {
    if (identity !== "account" || !subject || !transport?.resolve) {
      return null;
    }
    const ids = [...new Set(productIds.map(String))];
    if (ids.length === 0 || ids.length > 12) {
      return null;
    }
    const token = await getFreshCsrf(subject, identityGeneration);
    if (!token) {
      return null;
    }
    const request = createRequestSignal();
    let result;
    try {
      result = await transport.resolve({
        body: {
          protocolVersion: SCHEMA_VERSION,
          expectedSubject: subject,
          csrfToken: token,
          productIds: ids,
        },
        signal: request.signal,
      });
    } catch {
      return null;
    } finally {
      request.cleanup();
    }
    const parsed = await parseTransportResult(result).catch(() => null);
    if (parsed?.status !== 200 || !Array.isArray(parsed.body?.items)) {
      return null;
    }
    return parsed.body.items;
  }

  async function importSelected(productIds) {
    if (identity !== "account" || !subject) {
      return false;
    }
    await withStateLock(() => {
      const scope = ensureScope(subject);
      for (const productId of productIds.map(String)) {
        scope.drafts = applyAccountDraft(scope.drafts, {
          productId,
          present: true,
        });
      }
      scope.updatedAt = adapters.now();
      persist();
    });
    scheduleDrain(subject);
    notify();
    return true;
  }

  async function discardImport() {
    await withStateLock(() => {
      const scope = document.accounts[subject];
      if (scope?.guestImport) {
        scope.guestImport.items = [];
        scope.guestImport.rejectedItems = [];
        scope.guestImport.status = "complete";
        scope.updatedAt = adapters.now();
        persist();
      }
    });
    notify();
  }

  async function retryNeedsReview() {
    if (identity !== "account" || !subject) {
      return false;
    }
    await withStateLock(() => {
      const scope = ensureScope(subject);
      const batch = scope.needsReview;
      if (!batch) {
        return;
      }
      for (const operation of batch.operations) {
        scope.drafts = applyAccountDraft(scope.drafts, {
          productId: operation.productId,
          present: operation.present,
        });
      }
      scope.needsReview = null;
      scope.updatedAt = adapters.now();
      persist();
    });
    scheduleDrain(subject);
    notify();
    return true;
  }

  async function discardNeedsReview() {
    await withStateLock(() => {
      const scope = document.accounts[subject];
      if (scope) {
        scope.needsReview = null;
        scope.updatedAt = adapters.now();
        persist();
      }
    });
    notify();
  }

  function start() {
    if (disposed || !syncEnabled) {
      return;
    }
    bindLifecycle();
    if (identity === "unknown") {
      void bootstrap();
    }
  }

  function dispose() {
    disposed = true;
    unbindLifecycle();
    for (const timer of drainDebounces.values()) {
      adapters.clearTimeout(timer);
    }
    drainDebounces.clear();
    for (const timer of retryTimers.values()) {
      adapters.clearTimeout(timer);
    }
    retryTimers.clear();
  }

  refresh();

  const ready = (() => {
    if (syncEnabled && transport && autoBootstrap) {
      start();
      return bootstrapPromise ?? Promise.resolve();
    }
    return Promise.resolve();
  })();

  return Object.freeze({
    ready,
    start,
    dispose,
    getSnapshot,
    has,
    set,
    toggle,
    subscribe,
    setIdentity,
    invalidateIdentity,
    revalidate,
    bootstrap,
    drain,
    resolveHandles,
    importSelected,
    discardImport,
    retryNeedsReview,
    discardNeedsReview,
    get storageKey() {
      return storageKey(shopId);
    },
  });
}

export function validateRelativeProxyPath(value) {
  if (typeof value !== "string") {
    return null;
  }
  const raw = value.trim();
  if (
    !raw ||
    /[\u0000-\u001F\\]/.test(raw) ||
    /^\/\//.test(raw) ||
    /^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(raw) ||
    /[?#]/.test(raw)
  ) {
    return null;
  }
  const segments = raw.split("/");
  if (segments.some((segment) => segment === "..")) {
    return null;
  }
  const normalized = raw
    .split("/")
    .filter((segment) => segment && segment !== ".")
    .join("/");
  return `/${normalized}`;
}

export function validateRelativeRootUrl(value) {
  if (value === undefined || value === null || value === "") {
    return "/";
  }
  if (typeof value !== "string") {
    return null;
  }
  const raw = value.trim();
  if (
    !raw ||
    /[\u0000-\u001F\\]/.test(raw) ||
    /^\/\//.test(raw) ||
    /^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(raw) ||
    /[?#]/.test(raw)
  ) {
    return null;
  }
  const segments = raw.split("/");
  if (segments.some((segment) => segment === "..")) {
    return null;
  }
  const normalized = raw
    .split("/")
    .filter((segment) => segment && segment !== ".")
    .join("/");
  return normalized ? `/${normalized}` : "/";
}

export function joinRelativeUrl(root, path) {
  const normalizedRoot = validateRelativeRootUrl(root);
  const normalizedPath = validateRelativeProxyPath(path);
  if (!normalizedRoot || !normalizedPath) {
    return null;
  }
  if (normalizedRoot === "/") {
    return normalizedPath;
  }
  return `${normalizedRoot}${normalizedPath}`;
}

export function isSafeRelativeUrl(value) {
  return typeof value === "string" && validateRelativeProxyPath(value) !== null;
}

export function normalizeFavoritesConfig(input = {}) {
  const strings =
    input.strings && typeof input.strings === "object" ? input.strings : {};
  return {
    protocolVersion: 2,
    shopId: String(input.shopId ?? ""),
    featureEnabled: Boolean(input.featureEnabled),
    accountSync: Boolean(input.accountSync),
    proxyPath:
      validateRelativeProxyPath(input.proxyPath) ?? "/apps/nma-favorites",
    rootUrl: validateRelativeRootUrl(input.rootUrl) ?? "/",
    favoritesUrl: isSafeRelativeUrl(input.favoritesUrl)
      ? validateRelativeProxyPath(input.favoritesUrl)
      : "",
    loginUrl: isSafeRelativeUrl(input.loginUrl)
      ? validateRelativeProxyPath(input.loginUrl)
      : "/account/login",
    strings,
  };
}

export function formatInterpolatedMessage(template, params = {}) {
  if (typeof template !== "string") {
    return "";
  }
  return template.replace(/\{\{\s*([\w-]+)\s*\}\}/g, (_match, key) => {
    return Object.prototype.hasOwnProperty.call(params, key)
      ? String(params[key])
      : "";
  });
}

if (
  typeof globalThis.document !== "undefined" &&
  globalThis.customElements &&
  globalThis.HTMLElement
) {
  const RUNTIME_EVENT = "nma-favorites:manager-ready";
  const registry = new Map();
  const linkRegistry = new Set();
  let runtimeManager = null;
  let runtimeConfig = null;
  let runtimeSubscription = null;

  function getRuntimeManager() {
    return runtimeManager;
  }

  function registerElement(element) {
    if (element.tagName === "NMA-FAVORITES-LINK") {
      linkRegistry.add(element);
      element._nmaRegistryKey = "__link__";
      return;
    }
    const key = element.dataset?.productId;
    if (!key) {
      return;
    }
    if (!registry.has(key)) {
      registry.set(key, new Set());
    }
    registry.get(key).add(element);
    element._nmaRegistryKey = key;
  }

  function unregisterElement(element) {
    const key = element._nmaRegistryKey;
    if (!key) {
      return;
    }
    if (key === "__link__") {
      linkRegistry.delete(element);
      element._nmaRegistryKey = null;
      return;
    }
    const set = registry.get(key);
    if (set) {
      set.delete(element);
      if (set.size === 0) {
        registry.delete(key);
      }
    }
    element._nmaRegistryKey = null;
  }

  function announce(message) {
    const live = globalThis.document.getElementById(
      "nma-favorites-live-region",
    );
    if (live) {
      live.textContent = message;
    }
  }

  function updateButton(element) {
    if (!runtimeManager) {
      return;
    }
    const productId = element.dataset?.productId;
    const pressed = productId ? runtimeManager.has(productId) : false;
    const button = element.querySelector(".nma-favorite__button");
    const firstRender = element._nmaInitialized !== true;
    element._nmaInitialized = true;
    if (button) {
      const wasPressed = button.getAttribute("aria-pressed") === "true";
      button.setAttribute("aria-pressed", pressed ? "true" : "false");
      button.classList.toggle("is-selected", pressed);
      if (pressed && !wasPressed && !firstRender) {
        button.classList.remove("nma-favorite__button--pop");
        // Restart the spring animation even when it was just played.
        void button.offsetWidth;
        button.classList.add("nma-favorite__button--pop");
      }
    }
    const card = element.closest(".card-wrapper") || element.closest(".card__inner");
    if (card && !element.closest("[data-nma-favorites-page]")) {
      card.classList.toggle("nma-favorites-card--active", pressed);
    }
    const label = element.querySelector(".visually-hidden");
    if (label) {
      label.textContent = formatInterpolatedMessage(
        runtimeConfig?.strings?.toggle_label ?? "Favorite: {{ product }}",
        { product: element.dataset?.productTitle ?? "" },
      );
    }
  }

  function updateLink(element) {
    if (!runtimeConfig) {
      return;
    }
    const snapshot = runtimeManager?.getSnapshot();
    const count = snapshot?.identity === "unknown" ? null : snapshot?.count;
    const countElement = element.querySelector(".nma-favorites-link__count");
    if (countElement) {
      countElement.textContent = count === null ? "" : String(count);
    }
    const label = element.querySelector(".visually-hidden");
    if (label) {
      label.textContent = formatInterpolatedMessage(
        runtimeConfig.strings?.count_label ?? "Favorites ({{ count }})",
        { count: count ?? 0 },
      );
    }
    const anchor = element.querySelector("a");
    if (anchor && runtimeConfig.favoritesUrl) {
      anchor.setAttribute("href", runtimeConfig.favoritesUrl);
    }
  }

  function notifyElements() {
    for (const elements of registry.values()) {
      for (const element of elements) {
        updateButton(element);
      }
    }
    for (const element of linkRegistry) {
      updateLink(element);
    }
  }

  class NmaFavoriteButtonElement extends HTMLElement {
    static get observedAttributes() {
      return ["data-product-id", "data-product-handle", "data-product-title"];
    }

    connectedCallback() {
      this._nmaOnClick = (event) => {
        const productId = this.dataset?.productId;
        const manager = getRuntimeManager();
        if (!productId || !manager) {
          return;
        }
        event.stopPropagation();
        const wasPressed = manager.has(productId);
        manager.toggle({
          productId,
          handle: this.dataset.productHandle,
          title: this.dataset.productTitle,
        });
        announce(
          formatInterpolatedMessage(
            runtimeConfig?.strings?.[wasPressed ? "removed" : "added"] ??
              (wasPressed ? "Removed from favorites" : "Added to favorites"),
          ),
        );
      };
      registerElement(this);
      this.addEventListener("click", this._nmaOnClick);
      this._nmaOnAnimationEnd = (event) => {
        if (event.animationName !== "nma-favorite-pop") {
          return;
        }
        const button = this.querySelector(".nma-favorite__button");
        if (button) {
          button.classList.remove("nma-favorite__button--pop");
        }
      };
      this.addEventListener("animationend", this._nmaOnAnimationEnd);
      updateButton(this);
    }

    disconnectedCallback() {
      unregisterElement(this);
      this.removeEventListener("click", this._nmaOnClick);
      this.removeEventListener("animationend", this._nmaOnAnimationEnd);
    }

    attributeChangedCallback(name, oldValue, newValue) {
      if (name === "data-product-id" && oldValue !== newValue) {
        unregisterElement(this);
        registerElement(this);
        updateButton(this);
      }
    }
  }

  class NmaFavoritesLinkElement extends HTMLElement {
    connectedCallback() {
      registerElement(this);
      updateLink(this);
    }

    disconnectedCallback() {
      unregisterElement(this);
    }
  }

  function defineRuntimeElements() {
    if (!globalThis.customElements.get("nma-favorite-button")) {
      globalThis.customElements.define(
        "nma-favorite-button",
        NmaFavoriteButtonElement,
      );
    }
    if (!globalThis.customElements.get("nma-favorites-link")) {
      globalThis.customElements.define(
        "nma-favorites-link",
        NmaFavoritesLinkElement,
      );
    }
  }

  function bootstrapRuntime(config) {
    const normalized = normalizeFavoritesConfig(config);
    if (!normalized.featureEnabled || !normalized.shopId) {
      return null;
    }
    if (runtimeManager) {
      return runtimeManager;
    }

    const statePath = joinRelativeUrl(
      normalized.rootUrl,
      `${normalized.proxyPath}/v2/state`,
    );
    const mutationsPath = joinRelativeUrl(
      normalized.rootUrl,
      `${normalized.proxyPath}/v2/mutations`,
    );
    const resolvePath = joinRelativeUrl(
      normalized.rootUrl,
      `${normalized.proxyPath}/v2/resolve`,
    );
    if (!statePath || !mutationsPath || !resolvePath) {
      runtimeConfig = null;
      return null;
    }

    runtimeConfig = normalized;
    defineRuntimeElements();

    runtimeManager = createFavoritesManager({
      shopId: normalized.shopId,
      capabilities: { maxFavorites: 1000 },
      transport: createBrowserTransport({
        statePath,
        mutationsPath,
        resolvePath,
      }),
      syncEnabled: normalized.accountSync,
      autoBootstrap: normalized.accountSync,
    });
    runtimeSubscription = runtimeManager.subscribe(notifyElements);

    if (normalized.accountSync) {
      runtimeManager.start();
    } else {
      runtimeManager.setIdentity("guest");
    }

    globalThis.document.dispatchEvent(
      new CustomEvent(RUNTIME_EVENT, { detail: { manager: runtimeManager } }),
    );
    notifyElements();
    return runtimeManager;
  }

  function readConfigElement() {
    const node = globalThis.document.getElementById("nma-favorites-config");
    if (!node || !node.textContent) {
      return null;
    }
    try {
      return JSON.parse(node.textContent);
    } catch {
      return null;
    }
  }

  function bootstrapFromDocument() {
    const config = readConfigElement();
    if (config) {
      bootstrapRuntime(config);
    }
  }

  globalThis.NMAFavorites = {
    bootstrap: bootstrapRuntime,
    getManager: getRuntimeManager,
  };

  if (globalThis.document.readyState === "loading") {
    globalThis.document.addEventListener(
      "DOMContentLoaded",
      bootstrapFromDocument,
      { once: true },
    );
  } else {
    bootstrapFromDocument();
  }
}
